import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Download } from "lucide-react";
import {
  isStandalone,
  isIos,
  isIosNonSafari,
  inAppBrowserName,
  copyCurrentUrl,
} from "./PWAInstallPrompt";

/**
 * "Install Web App" — Tools-menu entry support.
 *
 * canOfferInstall(): false when already running as an installed app,
 * previously installed from this browser, or inside an in-app browser
 * (Instagram/Facebook/TikTok WebViews can't install anything) — the
 * menu item is hidden entirely in those cases per owner preference.
 *
 * InstallGuideModal: browser-specific step-by-step instructions shown
 * when the native one-tap install prompt isn't available.
 */

export function canOfferInstall() {
  if (typeof window === "undefined") return false;
  if (isStandalone()) return false;
  if (localStorage.getItem("pepura_pwa_installed") === "1") return false;
  if (inAppBrowserName()) return false;
  return true;
}

function detectEnv() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (isIos()) return isIosNonSafari() ? "ios-other" : "ios-safari";
  if (/Android/i.test(ua)) return "android";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "mac-safari";
  return "generic";
}

const GUIDES = {
  "ios-safari": {
    title: "Install on your iPhone",
    steps: [
      "Tap the Share button (the square with an arrow pointing up) at the bottom of Safari.",
      "Scroll down and tap \u201cAdd to Home Screen\u201d.",
      "Tap \u201cAdd\u201d in the top-right corner \u2014 Pepura Labs appears on your home screen like a regular app.",
    ],
  },
  "ios-other": {
    title: "Install via Safari",
    copyButton: true,
    steps: [
      "On iPhone, only Safari can install web apps.",
      "Copy this page\u2019s link below, open Safari and paste it in the address bar.",
      "Tap the Share button, then \u201cAdd to Home Screen\u201d, then \u201cAdd\u201d.",
    ],
  },
  android: {
    title: "Install on Android",
    steps: [
      "Tap the \u22ee menu in the top-right corner of your browser.",
      "Choose \u201cAdd to Home screen\u201d (or \u201cInstall app\u201d).",
      "Confirm \u2014 Pepura Labs is added to your home screen like a regular app.",
    ],
  },
  chrome: {
    title: "Install in Chrome",
    steps: [
      "Look for the install icon at the right end of the address bar (a small screen with a down arrow) and click it.",
      "If you don\u2019t see it, open the \u22ee menu \u2192 \u201cCast, save and share\u201d \u2192 \u201cInstall page as app\u2026\u201d.",
      "Click \u201cInstall\u201d \u2014 Pepura Labs opens in its own window from now on.",
    ],
  },
  edge: {
    title: "Install in Edge",
    steps: [
      "Open the \u2026 menu in the top-right corner.",
      "Choose \u201cApps\u201d \u2192 \u201cInstall this site as an app\u201d.",
      "Click \u201cInstall\u201d.",
    ],
  },
  "mac-safari": {
    title: "Install in Safari",
    steps: [
      "In Safari\u2019s menu bar, choose File \u2192 \u201cAdd to Dock\u2026\u201d.",
      "Click \u201cAdd\u201d \u2014 Pepura Labs appears in your Dock like a regular app.",
    ],
  },
  firefox: {
    title: "Firefox can\u2019t install web apps",
    steps: [
      "Desktop Firefox doesn\u2019t support installing web apps.",
      "Open this site in Chrome, Edge or Safari and choose \u201cInstall Web App\u201d from the Tools menu there.",
    ],
  },
  generic: {
    title: "Install Pepura Labs",
    steps: [
      "Open your browser\u2019s main menu.",
      "Look for \u201cInstall app\u201d or \u201cAdd to Home screen\u201d and confirm.",
    ],
  },
};

export function InstallGuideModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!open || typeof document === "undefined") return null;
  const guide = GUIDES[detectEnv()] || GUIDES.generic;

  const handleCopy = async () => {
    const ok = await copyCurrentUrl();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return createPortal(
    <div
      data-testid="install-guide-modal"
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-label="How to install the Pepura Labs web app"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-xl border border-white/15 bg-[#0f1418] p-6 text-white shadow-2xl">
        <button
          type="button"
          data-testid="install-guide-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 hover:text-white hover:bg-white/10"
        >
          <X size={15} />
        </button>
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#20a6da]/15 text-[#20a6da] shrink-0">
            <Download size={18} strokeWidth={2.25} />
          </span>
          <h2 className="font-display text-lg font-[700] tracking-tight">
            {guide.title}
          </h2>
        </div>
        <ol className="mt-4 space-y-3">
          {guide.steps.map((s, i) => (
            <li key={i} className="flex gap-3 text-sm leading-snug text-[#bfcde3]">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-bold text-white">
                {i + 1}
              </span>
              {s}
            </li>
          ))}
        </ol>
        {guide.copyButton && (
          <button
            type="button"
            data-testid="install-guide-copy-link"
            onClick={handleCopy}
            className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#20a6da] text-[#0a0a0a] hover:bg-[#1c92bf] px-4 py-2 text-xs font-semibold uppercase tracking-wide"
          >
            {copied ? "Link copied \u2713" : "Copy link for Safari"}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
